import { WineWidgetModel } from '../../../models/WineWidgetModel';

/* The `interface WineTagProps` is defining the type of props that the `WineTags` component expects. It
specifies that the `wine` prop should be of type `WineModel`. This allows the component to receive
the `wine` prop and access its properties and methods. */
interface WineTagProps {
  wine: WineWidgetModel;
}

/**
 * The WineTags component renders the first tags of a wine and displays the number of hidden tags.
 * @param {WineTagProps}  - - `wine`: The wine object that contains information about the wine.
 * @returns The WineTags component is returning a div element with the class name 'absolute top-1
 * right-0 w-[25%] sm:w-[30%]'. Inside the div, it renders the FirstTags component and the
 * NumberOfHiddenTags component, passing the wine prop to both components.
 */
export const WineTags = ({ wine }: WineTagProps) => {
  const tagClasses = wine.isManufacturer ? 'absolute top-12 right-0 w-[40%]' : 'absolute top-4 right-0 w-[40%]';

  return (
    <div className={tagClasses}>
      <FirstTags wine={wine} />
      <NumberOfHiddenTags wine={wine} />
    </div>
  );
};

/**
 * The FirstTags component displays a count of tags.
 * @param {WineTagProps}  - The `FirstTags` component takes a prop called `wine` which is an
 * object with the following structure:
 * @returns The component `FirstTags` returns tags based on the given number
 */
const FirstTags = ({ wine }: WineTagProps) => {
  return (
    <>
      {wine.tags &&
        wine.tags.slice(0, 3).map((tag, index) => (
          <div
            className={`font-body flex items-center jusitfy-center font-semibold text-[10px] my-1 text-center z-2 bg-white w-min max-w-full px-[8px] absolute overflow-hidden whitespace-nowrap text-ellipsis`}
            key={index}
            style={{
              top: `${index * 1.6}rem`,
            }}
          >
            <h1 className='font-[600] text-[10px] font-body text-center my-[3px]'>{tag.toUpperCase()}</h1>
          </div>
        ))}
    </>
  );
};

/**
 * The NumberOfHiddenTags component displays a count of hidden tags if the number of tags exceeds 3.
 * @param {WineTagProps}  - The `NumberOfHiddenTags` component takes a prop called `wine` which is an
 * object with the following structure:
 * @returns The component `NumberOfHiddenTags` returns a `div` element with the text
 * `+{wine.tags.length - 3}` if the length of `wine.tags` is greater than 3.
 */
const NumberOfHiddenTags = ({ wine }: WineTagProps) => {
  return (
    <>
      {wine.tags && wine.tags.length > 3 && (
        <div
          className={`border border-sm font-semibold font-body min-[350px]:text-[10px] sm:text-[12px] my-1 text-center z-2 bg-white opacity-80 w-min max-w-full px-0.5 absolute overflow-hidden whitespace-nowrap text-ellipsis`}
          style={{
            top: '4.8rem',
          }}
        >
          <h1 className='font-semibold font-body text-center'>+{wine.tags.length - 3}</h1>
        </div>
      )}
    </>
  );
};
